import type { CloudflareEnv } from "../env";
import { getCloudflareContext } from "@opennextjs/cloudflare";

function getNodeEnvValue(key: string) {
  if (key === "DB") {
    return undefined;
  }

  return process.env[key];
}

let cachedEnv: CloudflareEnv | undefined;

function getCloudflareEnvSync() {
  try {
    return (cachedEnv ??= getCloudflareContext().env as CloudflareEnv);
  } catch {
    return undefined;
  }
}

type EnvValue = CloudflareEnv[keyof CloudflareEnv];

function createEnvProxy(getValue: (key: keyof CloudflareEnv & string) => EnvValue | undefined) {
  return new Proxy({} as CloudflareEnv, {
    get(_target, prop) {
      if (typeof prop !== "string") {
        return undefined;
      }

      return getValue(prop as keyof CloudflareEnv & string);
    },
    has(_target, prop) {
      if (typeof prop !== "string") {
        return false;
      }

      return getValue(prop as keyof CloudflareEnv & string) !== undefined;
    },
  });
}

function resolveEnvValue(key: keyof CloudflareEnv & string): EnvValue | undefined {
  const nodeValue = getNodeEnvValue(key);
  if (nodeValue !== undefined) {
    return nodeValue as EnvValue;
  }

  return getCloudflareEnvSync()?.[key as keyof CloudflareEnv];
}

// Next.js local dev runs in Node.js, where env vars are exposed on process.env.
// In the Cloudflare runtime, fall back to OpenNext's Cloudflare context bindings.
// For static routes (ISR/SSG), use getEnvAsync() so OpenNext can resolve bindings
// with the async Cloudflare context API.
export async function getEnvAsync() {
  const cloudflareEnv = (await getCloudflareContext({ async: true })).env as CloudflareEnv;

  cachedEnv ??= cloudflareEnv;

  return createEnvProxy((key) => {
    const nodeValue = getNodeEnvValue(key);
    if (nodeValue !== undefined) {
      return nodeValue;
    }

    return cloudflareEnv[key as keyof CloudflareEnv];
  });
}

export const env = createEnvProxy(resolveEnvValue);

export async function getCacheAsync() {
  const { ctx } = await getCloudflareContext({ async: true });
  return ctx.cache;
}
